/**
 * CreditEngineConfidenceGate — /admin/credit-engine/confidence-gate
 *
 * Wave 5-Late Credit Engine propagation — anchor pattern applied.
 * DECISION_COLOR Tailwind class strings replaced with a
 * DECISION_TONE map → DS `Badge` (pass → successMuted, warn →
 * warning, fail_closed → danger). `scoreColor(v)` now returns a
 * CSS var color string using the byte-exact thresholds (0.85 / 0.6).
 * Filter pill row → sharp GhostButton with `--ds-radius-input`
 * accent-soft active state. RowDetail sub-panel uses hairline
 * `--ds-color-admin-border`.
 *
 * Business logic (filter + sort + pagination + expandable RowDetail
 * with review-queue link) preserved BYTE-EXACT. All testids and
 * threshold values unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, ChevronDown, ChevronRight, ExternalLink, ArrowUpDown, Filter, ListTree,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Badge } from "@/design-system/components/Badge";
import { GhostButton, Section, SegmentedFilter, Stat } from "@/admin/_primitives";

const PAGE_LIMIT = 25;

const DECISION_TONE = {
  pass: "successMuted",
  warn: "warning",
  fail_closed: "danger",
};

const DECISION_LABEL = {
  pass: "PASS",
  warn: "WARN",
  fail_closed: "FAIL-CLOSED",
};

function fmtPct(v) {
  if (v == null) return "—";
  return `${Number(v).toFixed(1)}%`;
}

function fmtScore(v) {
  if (v == null) return "—";
  return Number(v).toFixed(2);
}

/* Threshold values (0.85 / 0.6) preserved BYTE-EXACT — only the
   returned color values swap onto admin tokens. */
function scoreColor(v) {
  if (v == null) return "var(--ds-color-ink-muted)";
  if (v >= 0.85) return "var(--ds-color-success-muted)";
  if (v >= 0.6) return "var(--ds-color-warning)";
  return "var(--ds-color-danger)";
}




/* Segmented filter — visual mirror of the anchor filter chrome. */

export default function CreditEngineConfidenceGate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("-confidence");
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        filter,
        sort,
        offset: String(offset),
        limit: String(PAGE_LIMIT),
      });
      const res = await api.get(
        `/admin/credit-engine/confidence-gate?${params.toString()}`,
      );
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter, sort, offset]);

  useEffect(() => {
    reload();
  }, [reload]);

  function toggleSort() {
    setSort((s) => (s === "-confidence" ? "confidence" : "-confidence"));
    setOffset(0);
  }

  function setFilterAndReset(v) {
    setFilter(v);
    setOffset(0);
  }

  const items = data?.items || [];
  const total = data?.total || 0;
  const filteredTotal = data?.filtered_total ?? total;
  const passN = data?.pass_count ?? 0;
  const warnN = data?.warn_count ?? 0;
  const failN = data?.fail_closed_count ?? 0;
  const avgConf = data?.avg_confidence;

  const filterOptions = [
    { value: "all", label: "All" },
    { value: "pass", label: "Pass" },
    { value: "warn", label: "Warn" },
    { value: "fail_closed", label: "Fail-closed" },
  ];

  return (
    <AdminLayout
      title="Confidence gate diagnostics"
      subtitle="Per-report confidence gate scoring — pass / warn / fail-closed"
      actions={
        <GhostButton onClick={reload} data-testid="cg-diag-reload-btn" className="!py-1 !text-[11px]">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </GhostButton>
      }
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Confidence gate diagnostics"
          purpose="Per-report health signal for the deterministic extraction + normalization pipeline. Shows which reports parsed cleanly, which parsed with warnings, and which failed closed."
          configures="Read-only diagnostic. Toggle filter/sort/expand to inspect individual reports. Click a review-queue entry to jump to the moderation view."
          impact="No user-facing impact — this is an operator tool. Fail-closed reports have already been blocked from letter generation and analyst runs."
          safety="Read-only. PII: user identifiers shown masked; report IDs are Mongo ObjectIds only."
        />
      </div>

      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3"
        data-testid="cg-diag-agg-stats"
      >
        <Stat testid="cg-stat-total" label="Total reports" value={total} highlight />
        <Stat testid="cg-stat-pass" label="Pass" value={passN} />
        <Stat testid="cg-stat-warn" label="Warn" value={warnN} />
        <Stat testid="cg-stat-fail" label="Fail-closed" value={failN} />
        <Stat
          testid="cg-stat-avg"
          label="Avg confidence"
          value={avgConf == null ? "—" : Number(avgConf).toFixed(2)}
        />
      </div>

      <Section title="Filter" icon={Filter} testid="cg-diag-filter-section">
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedFilter
            options={filterOptions}
            value={filter}
            onChange={setFilterAndReset}
            testIdPrefix="cg-filter"
          />
          <GhostButton onClick={toggleSort} data-testid="cg-sort-btn" className="!py-1 !text-[11px]">
            <ArrowUpDown className="w-3 h-3" />
            Sort by confidence {sort === "-confidence" ? "↓" : "↑"}
          </GhostButton>
          <div
            className="text-[11px] tabular-nums ml-auto"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Showing {items.length} of {filteredTotal} (filtered) · offset {offset}
          </div>
        </div>
      </Section>

      {loading ? (
        <div className="text-[13px] py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>
          Loading confidence gate data…
        </div>
      ) : (
        <Section
          title="Per-report confidence"
          icon={ListTree}
          testid="cg-diag-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto" data-testid="cg-diag-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2 font-semibold w-8"></th>
                  <th className="px-4 py-2 font-semibold">Confidence</th>
                  <th className="px-4 py-2 font-semibold">OCR conf</th>
                  <th className="px-4 py-2 font-semibold">Bureau preservation</th>
                  <th className="px-4 py-2 font-semibold">Extraction %</th>
                  <th className="px-4 py-2 font-semibold text-right">Findings</th>
                  <th className="px-4 py-2 font-semibold">Missing fields</th>
                  <th className="px-4 py-2 font-semibold">Decision</th>
                  <th className="px-4 py-2 font-semibold">Report</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const isOpen = expandedId === row.report_id;
                  const isLast = idx === items.length - 1 && !isOpen;
                  return (
                    <RowGroup
                      key={row.report_id}
                      row={row}
                      isOpen={isOpen}
                      isLast={isLast}
                      onToggle={() => setExpandedId(isOpen ? null : row.report_id)}
                    />
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                      data-testid="cg-diag-empty"
                    >
                      {total === 0
                        ? "No confidence-gate scored reports yet. The gate is evaluated at import time; reports imported before Phase 5D remain outside this table."
                        : "No reports match the current filter."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <div
        className="flex items-center justify-between mt-2 text-[11px] tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <span>Page offset {offset}</span>
        <div className="flex gap-1.5">
          <GhostButton
            onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
            disabled={offset === 0}
            data-testid="cg-diag-prev-btn"
            className="!py-1 !text-[11px]"
          >
            Prev
          </GhostButton>
          <GhostButton
            onClick={() => setOffset(offset + PAGE_LIMIT)}
            disabled={offset + PAGE_LIMIT >= filteredTotal}
            data-testid="cg-diag-next-btn"
            className="!py-1 !text-[11px]"
          >
            Next
          </GhostButton>
        </div>
      </div>
    </AdminLayout>
  );
}

function RowGroup({ row, isOpen, isLast, onToggle }) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer transition-colors"
        data-testid={`cg-row-${row.report_id}`}
        style={{
          borderBottom: isOpen ? "none" : (isLast ? "none" : "1px solid var(--ds-color-admin-border)"),
          transitionDuration: "var(--ds-duration-fast)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <td className="px-4 py-1.5" style={{ color: "var(--ds-color-ink-muted)" }}>
          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </td>
        <td
          className="px-4 py-1.5 tabular-nums font-mono font-medium"
          style={{ color: scoreColor(row.confidence_score) }}
        >
          {fmtScore(row.confidence_score)}
        </td>
        <td
          className="px-4 py-1.5 tabular-nums font-mono"
          style={{ color: scoreColor(row.ocr_confidence) }}
        >
          {row.ocr_confidence == null ? (
            <span className="text-[11px]" style={{ color: "var(--ds-color-ink-subtle)" }}>N/A</span>
          ) : (
            fmtScore(row.ocr_confidence)
          )}
        </td>
        <td
          className="px-4 py-1.5 tabular-nums font-mono"
          style={{ color: scoreColor(row.bureau_preservation_score) }}
        >
          {fmtScore(row.bureau_preservation_score)}
        </td>
        <td
          className="px-4 py-1.5 tabular-nums font-mono"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {fmtPct(row.extraction_completeness_pct)}
        </td>
        <td
          className="px-4 py-1.5 tabular-nums font-mono text-right"
          style={{ color: "var(--ds-color-ink)" }}
        >
          {row.finding_count ?? 0}
        </td>
        <td
          className="px-4 py-1.5 text-[11px] max-w-[220px] truncate"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {(row.missing_fields_top3 || []).length
            ? row.missing_fields_top3.join(", ")
            : "—"}
        </td>
        <td className="px-4 py-1.5">
          <Badge
            tone={DECISION_TONE[row.gate_decision] || "danger"}
            size="sm"
            shape="sharp"
            data-testid={`cg-row-decision-${row.report_id}`}
            className="uppercase tracking-[0.14em]"
          >
            {DECISION_LABEL[row.gate_decision] || row.gate_decision}
          </Badge>
        </td>
        <td
          className="px-4 py-1.5 font-mono text-[10.5px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {row.report_id?.slice(-8) || "—"}
        </td>
      </tr>
      {isOpen && (
        <tr
          style={{
            background: "var(--ds-color-admin-surface-muted)",
            borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
          }}
          data-testid={`cg-row-detail-${row.report_id}`}
        >
          <td colSpan="9" className="px-4 py-4">
            <RowDetail row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function RowDetail({ row }) {
  const brk = row.confidence_breakdown || {};
  const th = row.confidence_thresholds || {};

  const factors = [
    { key: "page_count_reconciliation_pass", label: "Page count reconciliation", type: "bool" },
    { key: "character_extraction_ratio", label: "Character extraction ratio", type: "ratio" },
    { key: "bureau_preservation_pass", label: "Bureau preservation", type: "bool" },
    { key: "account_count_sanity_pass", label: "Account count sanity", type: "bool" },
    { key: "field_completeness_ratio", label: "Field completeness", type: "ratio" },
    { key: "validator_error_penalty", label: "Validator error penalty", type: "ratio" },
  ];

  return (
    <div className="space-y-3">
      <div
        className="rounded-[var(--ds-radius-input)] p-3 text-[13px]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
        }}
      >
        <div
          className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          Recommended remediation
        </div>
        <div style={{ color: "var(--ds-color-ink)" }}>{row.recommended_remediation}</div>
        {row.confidence_user_message && (
          <div
            className="text-[11px] mt-2 italic"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            User-facing message: “{row.confidence_user_message}”
          </div>
        )}
      </div>

      <div>
        <div
          className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-2"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          Composite factor breakdown
          {th.pass != null && (
            <span
              className="ml-2 normal-case tracking-normal font-normal"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              Thresholds: PASS ≥ {th.pass} · WARN ≥ {th.warn}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {factors.map((f) => {
            const v = brk[f.key];
            let display, color;
            if (f.type === "bool") {
              display = v === true ? "PASS" : v === false ? "FAIL" : "—";
              color =
                v === true
                  ? "var(--ds-color-success-muted)"
                  : v === false
                  ? "var(--ds-color-danger)"
                  : "var(--ds-color-ink-muted)";
            } else {
              display = v == null ? "—" : Number(v).toFixed(2);
              color = scoreColor(v);
            }
            return (
              <div
                key={f.key}
                className="rounded-[var(--ds-radius-input)] px-3 py-2"
                style={{
                  background: "var(--ds-color-surface)",
                  border: "1px solid var(--ds-color-admin-border)",
                }}
                data-testid={`cg-factor-${row.report_id}-${f.key}`}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.12em] font-semibold"
                  style={{ color: "var(--ds-color-ink-subtle)" }}
                >
                  {f.label}
                </div>
                <div className="text-[13px] tabular-nums font-medium" style={{ color }}>
                  {display}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div
            className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Top missing tradeline fields
          </div>
          {(row.missing_fields_top3 || []).length ? (
            <ul className="text-[13px] list-disc pl-5" style={{ color: "var(--ds-color-ink)" }}>
              {row.missing_fields_top3.map((f) => <li key={f}>{f}</li>)}
            </ul>
          ) : (
            <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
              No fields with elevated null-rate on this report.
            </div>
          )}
        </div>
        <div>
          <div
            className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Review queue linkage
          </div>
          {row.review_queue_id ? (
            <div className="text-[13px]">
              <div style={{ color: "var(--ds-color-ink)" }}>
                Status:{" "}
                <span className="uppercase font-semibold">
                  {row.review_queue_status || "open"}
                </span>
              </div>
              {(row.review_queue_reasons || []).length > 0 && (
                <div className="text-[11px] mt-1" style={{ color: "var(--ds-color-ink-muted)" }}>
                  Reasons: {row.review_queue_reasons.join(", ")}
                </div>
              )}
              <Link
                to={`/admin/credit-engine/review-queue?open=${row.review_queue_id}`}
                className="inline-flex items-center gap-1 text-[11px] mt-2 uppercase tracking-[0.12em] font-semibold transition-colors"
                data-testid={`cg-review-link-${row.report_id}`}
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                <ExternalLink className="w-3 h-3" /> Open in review queue
              </Link>
            </div>
          ) : (
            <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
              Not in review queue — no escalation was triggered on this report.
            </div>
          )}
        </div>
      </div>

      <div
        className="text-[10px] font-mono tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        Report ID: <code>{row.report_id}</code> · User (masked):{" "}
        <code>{row.user_id_masked || "—"}</code>
        {row.filename ? <> · Source file: <code>{row.filename}</code></> : null}
        {row.source ? <> · Source: <code>{row.source}</code></> : null}
        {row.created_at ? <> · Imported: {row.created_at.slice(0, 19).replace("T", " ")} UTC</> : null}
      </div>
    </div>
  );
}
